import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Download as DownloadIcon, ShieldCheck } from "lucide-react";
import { api, API, fmt, previewSrc } from "../api";
import { Reveal } from "../components/Reveal";

export default function Download() {
  const { token } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/orders/${token}`).then((r) => setOrder(r.data)).catch(() => setError(true));
  }, [token]);

  if (error)
    return (
      <div className="mx-auto max-w-2xl px-6 py-32 text-center" data-testid="download-invalid">
        <h1 className="font-serif text-4xl text-ink">This download link isn't valid</h1>
        <p className="mt-4 text-sm text-ink/55">Check the link in your receipt email, or contact the studio for help.</p>
        <Link to="/contact" className="mt-6 inline-block text-sage underline underline-offset-4">Contact us</Link>
      </div>
    );
  if (!order)
    return <div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="animate-spin text-sage" /></div>;

  return (
    <div className="mx-auto max-w-3xl px-6 py-20 md:px-8" data-testid="download-page">
      <Reveal>
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-sage">Your order</p>
        <h1 className="mt-3 font-serif text-4xl font-light text-ink sm:text-5xl">Your files are ready</h1>
        <div className="mt-10 overflow-hidden rounded-[1.6rem] border border-sage/12 bg-white/80 backdrop-blur">
          {order.preview && (
            <img src={previewSrc(order.preview)} alt={order.product_name} className="h-48 w-full object-cover" />
          )}
          <div className="p-8">
            <h2 className="font-serif text-2xl text-ink" data-testid="download-product-name">{order.product_name}</h2>
            <p className="mt-2 text-sm text-ink/55">
              {fmt(order.amount, order.currency)} · receipt sent to {order.buyer_email}
            </p>
            {order.formats?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {order.formats.map((f) => (
                  <span key={f} className="rounded-full bg-sage-tint px-4 py-1.5 text-xs text-sage-deep">{f}</span>
                ))}
              </div>
            )}
            <div className="mt-8 flex flex-wrap gap-3">
              {(order.files?.length ? order.files : ["Download files"]).map((f, i) => (
                <a
                  key={i}
                  href={`${API}/orders/${token}/download/${i}`}
                  data-testid={`download-file-button-${i}`}
                  className="inline-flex items-center gap-3 rounded-full bg-sage px-7 py-4 text-sm font-medium text-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-sage-deep"
                >
                  <DownloadIcon size={16} /> {f}
                </a>
              ))}
            </div>
            <p className="mt-5 flex items-start gap-2 text-xs leading-relaxed text-ink/45">
              <ShieldCheck size={14} className="mt-0.5 shrink-0 text-sage" />
              This link is personal to you — {order.downloads} of {order.max_downloads} downloads used.
              Please don't share it; each link serves one customer.
            </p>
          </div>
        </div>
      </Reveal>
    </div>
  );
}
